import React from 'react';
import T from 'i18n';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon, CheckCircle } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectCurrentCaseId } from 'common/selectors';
import { getNonce } from 'utils/nonce';
import api from 'common/api';

interface FileItem {
  id: string;
  name: string;
  pageCount?: string;
  size?: number;
  type?: string;
  originalName?: string;
}

interface APIFileResult {
  id: string;
  name: string;
  originalName?: string;
  pageCount?: string;
  size?: number;
  type?: string;
  subtype?: string;
  confidentialityRing?: string | null;
  docDate?: string | null;
  semiPrivate?: boolean;
  hasAnnotations?: boolean;
  hasCrossRefs?: boolean;
  hasPublicHyperlinks?: boolean;
  hasSummary?: boolean;
  assignedTo?: string[];
  locations?: string[];
  matches?: any;
  bundleLocations?: any[];
  sequence?: number;
  compositeKey?: string | null;
  source?: string;
  rawDate?: string;
  altDate?: string | null;
  author?: string | null;
}

interface APISearchResponse {
  count: number;
  nonce: string;
  globalPageLookup: boolean;
  results: APIFileResult[];
  error?: string;
}

interface FilesSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (files: FileItem[]) => void;
  initialSelectedFiles: FileItem[];
}

export default function FilesSelectionDialog({
  open,
  onClose,
  onSave,
  initialSelectedFiles,
}: FilesSelectionDialogProps) {
  // File search state
  const [searchValue, setSearchValue] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<FileItem[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [selectedFiles, setSelectedFiles] = React.useState<FileItem[]>(initialSelectedFiles);

  // Get current case ID from Redux store
  const caseId = useSelector(selectCurrentCaseId);

  // Initialize selected files when dialog opens or initialSelectedFiles changes
  React.useEffect(() => {
    if (open) {
      setSelectedFiles(initialSelectedFiles);
    }
  }, [open, initialSelectedFiles]);

  // API search function for files
  const handleFileSearch = React.useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim() || !caseId) return;

      setIsSearching(true);
      setHasSearched(true);

      try {
        const nonce = getNonce(6);
        const endpoint = `/cases/${caseId}/doc-finder?nonce=${nonce}&search=${encodeURIComponent(searchTerm.trim())}`;

        const response = (await api.get(endpoint)) as APISearchResponse;

        if (response && response.error) {
          setSearchResults([]);
        } else if (response && response.nonce === nonce && response.results) {
          // Transform API response to FileItem format
          const transformedResults: FileItem[] = response.results.map((apiFile: APIFileResult) => ({
            id: apiFile.id,
            name: apiFile.name,
            originalName: apiFile.originalName,
            pageCount: apiFile.pageCount,
            size: apiFile.size,
            type: apiFile.type,
          }));
          setSearchResults(transformedResults);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error('File search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [caseId],
  );

  // Debounced search effect
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue.trim()) {
        handleFileSearch(searchValue);
      } else {
        setSearchResults([]);
        setHasSearched(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, handleFileSearch]);

  // File selection handlers
  const handleFileSelect = (selectedFile: FileItem) => {
    setSelectedFiles((prev) => {
      const isSelected = prev.some((f) => f.id === selectedFile.id);
      if (isSelected) {
        return prev.filter((f) => f.id !== selectedFile.id);
      } else {
        return [...prev, selectedFile];
      }
    });
  };

  // Handle file removal from the selected files list
  const handleFileRemove = (fileToRemove: FileItem) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileToRemove.id));
  };

  // Handle clearing all selected files
  const handleClearAllFiles = () => {
    setSelectedFiles([]);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleSaveFiles = () => {
    onSave(selectedFiles);
  };

  const handleCloseDialog = () => {
    // Reset search state when closing
    setSearchValue('');
    setSearchResults([]);
    setHasSearched(false);
    // Reset selectedFiles to initial state
    setSelectedFiles(initialSelectedFiles);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '60vh', width: '110rem', display: 'flex', flexDirection: 'column' },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">{T.translate('generic.selectFiles')}</Typography>
      </DialogTitle>
      <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'row', padding: 2, gap: 2 }}>
        {/* Left Column - Search TextField and Results */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Search TextField */}
          <TextField
            variant="outlined"
            size="small"
            placeholder={T.translate('generic.searchFiles')}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            autoFocus
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchValue ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} edge="end" size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Search Results */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {isSearching && (
              <Box display="flex" justifyContent="left" alignItems="left" p={2}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {T.translate('generic.searchingFiles')}...
                </Typography>
              </Box>
            )}

            {!isSearching && hasSearched && searchResults.length === 0 && (
              <Box display="flex" justifyContent="left" alignItems="left" p={2}>
                <Typography variant="body2" color="text.secondary">
                  {T.translate('generic.noFilesFound')} &quot;{searchValue}&quot;
                </Typography>
              </Box>
            )}

            {!isSearching && !hasSearched && searchValue.trim() === '' && (
              <Box display="flex" justifyContent="left" alignItems="left">
                <Typography variant="body2" color="text.secondary">
                  {T.translate('generic.startTypingForFiles')}...
                </Typography>
              </Box>
            )}

            {searchResults.length > 0 && (
              <List>
                {searchResults.map((searchFile) => (
                  <ListItem key={searchFile.id} disablePadding>
                    <ListItemButton
                      onClick={() => {
                        handleFileSelect(searchFile);
                      }}
                      sx={{
                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                        '&:last-child': { borderBottom: 'none' },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <ListItemText
                        primary={searchFile.name}
                        secondary={`ID: ${searchFile.id}${searchFile.pageCount ? ` • ${searchFile.pageCount} pages` : ''}`}
                        sx={{ flex: 1 }}
                      />
                      {selectedFiles.some((f) => f.id === searchFile.id) && (
                        <CheckCircle color="primary" sx={{ ml: 1 }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>

        {/* Right Column - Selected Files */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            pl: 2,
          }}
        >
          <Box
            sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
              {T.translate('generic.select')} ({selectedFiles.length})
            </Typography>
            {selectedFiles.length > 0 && (
              <Button
                size="small"
                onClick={handleClearAllFiles}
                sx={{ textTransform: 'none', fontSize: '12px' }}
              >
                {T.translate('generic.clearAll')}
              </Button>
            )}
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {selectedFiles.length === 0 ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ height: '100%', textAlign: 'center' }}
              >
                <Typography variant="body2" color="text.secondary">
                  {T.translate('generic.noFilesSelected')}
                  <br />
                  {T.translate('generic.searchFilesFromLeftPanel')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ overflow: 'auto', maxHeight: '100%' }}>
                <List sx={{ p: 0 }}>
                  {selectedFiles.map((selectedFile) => (
                    <ListItem
                      key={selectedFile.id}
                      sx={{
                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                        '&:last-child': { borderBottom: 'none' },
                        px: 1,
                        py: 1,
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ flex: 1, marginRight: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                              lineHeight: 1.4,
                              fontWeight: 'medium',
                            }}
                          >
                            {selectedFile.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {selectedFile.id}
                            {selectedFile.pageCount && ` • ${selectedFile.pageCount} pages`}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleFileRemove(selectedFile)}
                          sx={{
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCloseDialog}
          variant="contained"
          color="inherit"
          sx={{
            color: '#fff',
            bgcolor: 'text.disabled',
            ':hover': { bgcolor: 'text.secondary' },
          }}
        >
          {T.translate('generic.close')}
        </Button>
        <Button onClick={handleSaveFiles} variant="contained">
          {T.translate('generic.select')} ({selectedFiles.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
